// Small shared presentational pieces.
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { explorerAddress, explorerTx } from '../config/chain';
import { useChainNow } from '../hooks/chainClock';
import { formatCountdown, leadingZeroNibbles, shortAddress, shortHex } from '../lib/format';
import { temperColor } from '../lib/temper';

/** A 32-byte hash with its leading zero nibbles lit in a temper colour. */
export function HashText({ hash, color, truncate = false, className }: { hash: string; color?: string; truncate?: boolean; className?: string }) {
  const body = hash.startsWith('0x') ? hash.slice(2) : hash;
  const zeros = leadingZeroNibbles(hash);
  // a hash with z leading zero nibbles is roughly a 16^z-difficulty result
  const lit = color ?? temperColor(2 ** (4 * zeros));
  const rest = body.slice(zeros);
  const shown = truncate && rest.length > 20 ? `${rest.slice(0, 12)}…${rest.slice(-6)}` : rest;
  return (
    <span className={`mono ${className ?? ''}`} title={hash} style={{ overflowWrap: 'anywhere' }}>
      <span className="faint">0x</span>
      <span style={{ color: lit }}>{body.slice(0, zeros)}</span>
      <span>{shown}</span>
    </span>
  );
}

export function Addr({ address, to, full = false }: { address: string | null | undefined; to?: 'explorer' | 'token'; full?: boolean }) {
  if (!address) return <span className="faint">—</span>;
  const text = full ? address : shortAddress(address);
  const el = (
    <span className="mono" title={address}>
      {text}
    </span>
  );
  if (to === 'token') return <Link to={`/t/${address}`}>{el}</Link>;
  if (to === 'explorer') {
    const url = explorerAddress(address);
    if (url)
      return (
        <a href={url} target="_blank" rel="noreferrer">
          {el}
        </a>
      );
  }
  return el;
}

export function TxLink({ hash }: { hash: string }) {
  const url = explorerTx(hash);
  const el = (
    <span className="mono" title={hash}>
      {shortHex(hash, 10, 6)}
    </span>
  );
  return url ? (
    <a href={url} target="_blank" rel="noreferrer">
      {el}
    </a>
  ) : (
    el
  );
}

/** Launch image from metadata (pixelated, in a pixel frame), or a Pixelify monogram. */
export function LaunchImage({ src, symbol, size = 40 }: { src?: string | null; symbol: string; size?: number }) {
  const style = {
    width: size,
    height: size,
    flex: 'none' as const,
    border: '2px solid var(--shadow)',
    boxShadow: 'inset 0 0 0 2px var(--edge), 3px 3px 0 0 var(--shadow)',
  };
  if (src) return <img src={src} alt="" width={size} height={size} style={{ ...style, objectFit: 'cover', imageRendering: 'pixelated', padding: 2, background: 'var(--edge)' }} loading="lazy" />;
  return (
    <span
      aria-hidden="true"
      style={{
        ...style,
        display: 'inline-grid',
        placeItems: 'center',
        background: 'var(--night-2)',
        color: 'var(--molten)',
        fontFamily: 'var(--font-display)',
        fontWeight: 700,
        fontSize: Math.round(size * 0.5),
      }}
    >
      {symbol.slice(0, 1).toUpperCase()}
    </span>
  );
}

/** Countdown to a chain timestamp, driven by the chain clock. */
export function Countdown({ to, done }: { to: number; done?: ReactNode }) {
  const now = useChainNow(250);
  if (now === null) return <span className="faint">—</span>;
  const left = to - now;
  if (left <= 0 && done !== undefined) return <>{done}</>;
  return <span className="mono">{formatCountdown(left)}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="muted" style={{ margin: '8px 0' }}>{children}</p>;
}

export { Stat } from './pixel/Pixel';
